// Here we follow the idea that form builder has a specific shape for file attachments
// and we want to extract those files from any nested data structure.

import { set } from 'lodash';

type FileValueBase = {
	name?: string;
	size?: number;
	type?: string;
	extension?: string;
	data?: string;
	objectURL?: string;
	lastModified?: string;
	presignedUrl?: string; // URL from backend for displaying files
	id?: string; // Backend attachment ID for tracking changes
};
export interface FileAttachment extends FileValueBase {
	referenceKey: string; // Path to the field containing files (e.g., "avatar", "documents", "profile.certificates")
}

/**
 * Type guard to check if an object is a file attachment
 */
export const isFileAttachment = (obj: any): obj is FileAttachment => {
	// Check if required fields exist (without strict type checking)
	if (!obj || typeof obj !== 'object') {
		return false;
	}

	return (
		'name' in obj &&
		'size' in obj &&
		'type' in obj &&
		'extension' in obj &&
		('data' in obj || 'presignedUrl' in obj)
	);
};

/**
 * Check if a value is a file attachments array
 * A file attachments array contains objects that match the FileAttachment shape
 */
export const isFileAttachmentsArray = (
	value: any
): value is FileAttachment[] => {
	if (!Array.isArray(value) || value.length === 0) {
		return false;
	}

	// Check if all items in the array match the FileAttachment shape
	return value.every((item) => isFileAttachment(item));
};

/**
 * Recursively extract all file attachments from a nested object
 * Extracts ALL files (both new files and existing files with presignedUrl)
 * @param data - The data object to search through
 * @param currentPath - Current path in the object (used internally for referenceKey)
 * @param attachments - Accumulator array for found attachments (used internally)
 * @returns Flat array of all file attachments with their reference keys
 */
export const extractAttachments = (
	data: any,
	currentPath: string = '',
	attachments: FileAttachment[] = []
): FileAttachment[] => {
	// Handle null or undefined
	if (data === null || data === undefined) {
		return attachments;
	}

	// If it's a file attachments array, add ALL files (including those with presignedUrl)
	if (isFileAttachmentsArray(data)) {
		// Map all files and assign the field path as referenceKey
		const filesWithReferenceKey = data.map((file) => ({
			...file,
			referenceKey: currentPath, // Just use the field path, no index needed
		}));

		attachments.push(...filesWithReferenceKey);
		return attachments;
	}

	// If it's an array (but not file attachments), recurse through items
	if (Array.isArray(data)) {
		data.forEach((item, index) => {
			const path = currentPath ? `${currentPath}.${index}` : `${index}`;
			extractAttachments(item, path, attachments);
		});
		return attachments;
	}

	// If it's an object, recurse through values
	if (typeof data === 'object') {
		for (const key of Object.keys(data)) {
			const path = currentPath ? `${currentPath}.${key}` : key;
			extractAttachments(data[key], path, attachments);
		}
		return attachments;
	}

	// Primitive value, skip
	return attachments;
};

/**
 * Recursively remove file attachments from a nested object
 * Returns a new object with all file attachment arrays removed
 * @param data - The data object to clean
 * @returns New object without file attachments
 */
export const removeAttachments = (data: any): any => {
	// Handle null or undefined
	if (data === null || data === undefined) {
		return data;
	}

	// If it's a file attachments array, remove it (return undefined)
	if (isFileAttachmentsArray(data)) {
		return undefined;
	}

	// If it's an array (but not file attachments), recurse and filter
	if (Array.isArray(data)) {
		return data
			.map((item) => removeAttachments(item))
			.filter((item) => item !== undefined);
	}

	// If it's an object, recurse through values
	if (typeof data === 'object') {
		const cleaned: any = {};
		for (const key of Object.keys(data)) {
			const cleanedValue = removeAttachments(data[key]);
			// Only include the property if the value is not undefined
			if (cleanedValue !== undefined) {
				cleaned[key] = cleanedValue;
			}
		}
		return cleaned;
	}

	// Primitive value, keep as is
	return data;
};

/**
 * Extract all attachments and clean data in one operation
 * Returns ALL files (both new files and existing files with presignedUrl)
 * @param data - The data object to process
 * @returns Object containing all attachments and cleaned data
 */
export const processFormData = (
	data: any
): {
	attachments: FileAttachment[];
	cleanedData: any;
} => {
	const attachments = extractAttachments(data);
	const cleanedData = removeAttachments(data);

	return {
		attachments,
		cleanedData,
	};
};

/**
 * Backend attachment format from API response
 */
export interface BackendAttachment {
	id: string;
	fileMeta: {
		filename: string;
		format: string;
		size: number;
	};
	retrivalInfo: {
		presignedUrl: string;
		contentType: string;
	};
	referenceKey: string; // Lodash-style path to the field (e.g., "avatar", "documents", "submissions.0.files")
}

/**
 * Reconstruct original data structure from cleaned data and backend attachments
 * This is the reverse operation of processFormData - it merges backend attachments
 * back into the cleaned data at their original locations.
 *
 * @param cleanedData - The data without file attachments (from backend)
 * @param backendAttachments - Array of attachments with presigned URLs from backend
 * @returns Reconstructed data with files in their original locations
 *
 * @example
 * ```typescript
 * // Backend response
 * const response = {
 *   data: { name: "John", submissions: [{ id: 1 }] },
 *   attachments: [
 *     {
 *       fileMeta: { filename: "photo.jpg", format: "jpg", size: 47864 },
 *       retrivalInfo: { presignedUrl: "https://...", contentType: "image/jpeg" },
 *       referenceKey: "avatar"
 *     }
 *   ]
 * };
 *
 * const originalData = reconstructFormData(response.data, response.attachments);
 * // Result: { name: "John", avatar: [{ name: "photo.jpg", ... }], submissions: [...] }
 * ```
 */
export const reconstructFormData = (
	cleanedData: any,
	backendAttachments: BackendAttachment[]
): any => {
	// Deep clone the cleaned data to avoid mutations
	const reconstructed = JSON.parse(JSON.stringify(cleanedData));

	// Group attachments by their reference key (field path)
	const attachmentsByPath = new Map<string, BackendAttachment[]>();

	backendAttachments.forEach((attachment) => {
		const path = attachment.referenceKey;

		if (!attachmentsByPath.has(path)) {
			attachmentsByPath.set(path, []);
		}
		attachmentsByPath.get(path)!.push(attachment);
	});

	// For each group, reconstruct the file array at the correct location
	attachmentsByPath.forEach((attachments, path) => {
		// Convert backend format to frontend FileAttachment format
		const fileArray = attachments.map((attachment) => ({
			id: attachment.id, // Backend attachment ID
			name: attachment.fileMeta.filename,
			size: attachment.fileMeta.size,
			type: attachment.retrivalInfo.contentType,
			extension: attachment.fileMeta.format,
			lastModified: undefined, // Backend doesn't provide this
			data: undefined, // Backend doesn't send base64 data
			objectURL: undefined, // Local blob URL not available for backend files
			presignedUrl: attachment.retrivalInfo.presignedUrl, // Use presigned URL from backend
		}));

		// Set the file array at the correct location using lodash set
		set(reconstructed, path, fileArray);
	});

	return reconstructed;
};

/**
 * Compare two attachment arrays and get the IDs of removed files
 * Use case: User had 5 files, removed the 2nd and 3rd one. This function returns the IDs of those removed files.
 *
 * @param originalFiles - Original attachments array from backend (with IDs from reconstructFormData)
 * @param currentFiles - Current attachments array after user modifications (from form state)
 * @returns Array of attachment IDs that were removed
 *
 * @example
 * ```typescript
 * // Original attachments from backend (after reconstructFormData)
 * const originalFiles = [
 *   { id: 'file-1', name: 'a.jpg', presignedUrl: '...', ... },
 *   { id: 'file-2', name: 'b.jpg', presignedUrl: '...', ... },
 *   { id: 'file-3', name: 'c.jpg', presignedUrl: '...', ... },
 *   { id: 'file-4', name: 'd.jpg', presignedUrl: '...', ... },
 *   { id: 'file-5', name: 'e.jpg', presignedUrl: '...', ... },
 * ];
 *
 * // Current attachments after user removed 2nd and 3rd, and added a new one
 * const currentFiles = [
 *   { id: 'file-1', name: 'a.jpg', presignedUrl: '...', ... },
 *   { id: 'file-4', name: 'd.jpg', presignedUrl: '...', ... },
 *   { id: 'file-5', name: 'e.jpg', presignedUrl: '...', ... },
 *   { name: 'new.jpg', objectURL: 'blob:...', data: '...', ... }, // New file (no ID)
 * ];
 *
 * const removedIds = getRemovedFileIds(originalFiles, currentFiles);
 * // Returns: ['file-2', 'file-3']
 * ```
 */
export const getRemovedFileIds = (
	originalFiles: FileValueBase[],
	currentFiles: FileValueBase[]
): string[] => {
	// Get all IDs from original files (only files with IDs from backend)
	const originalIds = originalFiles
		.filter((file) => file.id !== undefined)
		.map((file) => file.id!);

	// Get all IDs from current files
	const currentIds = new Set(
		currentFiles.filter((file) => file.id !== undefined).map((file) => file.id!)
	);

	// Find IDs that are in original but not in current
	const removedIds = originalIds.filter((id) => !currentIds.has(id));

	return removedIds;
};

/**
 * Compare entire form data and get all removed file IDs across all fields
 * Recursively traverses the data structure to find all file arrays and compare them
 *
 * @param originalData - Original form data from backend (reconstructed with IDs)
 * @param currentData - Current form data after user modifications
 * @returns Array of all attachment IDs that were removed across all fields
 *
 * @example
 * ```typescript
 * const originalData = {
 *   avatar: [{ id: 'avatar-1', name: 'profile.jpg', ... }],
 *   documents: [
 *     { id: 'doc-1', name: 'resume.pdf', ... },
 *     { id: 'doc-2', name: 'certificate.pdf', ... },
 *   ],
 *   submissions: [
 *     {
 *       id: 1,
 *       files: [{ id: 'file-1', name: 'report.pdf', ... }]
 *     }
 *   ]
 * };
 *
 * const currentData = {
 *   avatar: [], // Removed avatar
 *   documents: [{ id: 'doc-1', name: 'resume.pdf', ... }], // Removed doc-2
 *   submissions: [
 *     {
 *       id: 1,
 *       files: [] // Removed file-1
 *     }
 *   ]
 * };
 *
 * const allRemovedIds = getAllRemovedFileIds(originalData, currentData);
 * // Returns: ['avatar-1', 'doc-2', 'file-1']
 * ```
 */
export const getAllRemovedFileIds = (
	originalData: any,
	currentData: any
): string[] => {
	const removedIds: string[] = [];

	const traverse = (original: any, current: any) => {
		// Handle null or undefined
		if (original === null || original === undefined) {
			return;
		}

		// If it's a file attachments array, compare them
		if (isFileAttachmentsArray(original)) {
			// Get current value (might be undefined, empty array, or file array)
			const currentArray = Array.isArray(current) ? current : [];
			const removed = getRemovedFileIds(original, currentArray);
			removedIds.push(...removed);
			return;
		}

		// If it's an array (but not file attachments), recurse through items
		if (Array.isArray(original)) {
			original.forEach((item, index) => {
				const currentItem = Array.isArray(current) ? current[index] : undefined;
				traverse(item, currentItem);
			});
			return;
		}

		// If it's an object, recurse through values
		if (typeof original === 'object') {
			for (const key of Object.keys(original)) {
				const currentValue =
					current && typeof current === 'object' ? current[key] : undefined;
				traverse(original[key], currentValue);
			}
			return;
		}

		// Primitive value, skip
	};

	traverse(originalData, currentData);
	return removedIds;
};
